import { Guid } from '@aglenergy/shared-library';
import { Build, h, JSX } from '@stencil/core';
import { store } from 'app/shared/store/store';
import { DataSpaSearchParams } from 'app/shared/utilities/data-spa-search-params.util';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { ProductCatalogueProduct } from 'app/shared/models/product-catalogue-product.model';
import { CommonLink } from 'app/shared/models/common-links/common-link.model';
import { GeneralContent } from 'app/shared/models/common-links/general-content.model';
import { GeneralContentCategoryItems } from 'app/shared/services/general-content/models/general-content-category-items.model';

export const isLocalDevelopment = (): boolean => {
  return (Build.isDev && window.location.hostname.indexOf('localhost') !== -1) as boolean;
};

export const getDataSpaSearchParams = (dataSpaParams: string): DataSpaSearchParams => {
  return dataSpaParams ? new DataSpaSearchParams(dataSpaParams) : null;
};

export const getHeaders = (isConfigServiceRequest = false, token = ''): HeadersInit => {
  const httpHeaders: HeadersInit = {
    'Content-Type': 'application/json; charset=utf-8',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Credentials': 'true',
    'Correlation-Id': Guid.newGuid(),
    'Api-Version': 'v1'
  };

  const mockApiValue = sessionStorage.getItem(StoreKeys.MockEligibility);
  if (mockApiValue?.length > 0) {
    httpHeaders['sales-mock'] = mockApiValue;
  }

  if (!isConfigServiceRequest) {
    const configData = store.get('configData');
    httpHeaders['Ocp-Apim-Subscription-Key'] = configData?.bffOcpApimSubscriptionKey;
    httpHeaders['Content-Mode'] = store.get('contentMode');
    httpHeaders['Authorization'] = token;
  }

  return httpHeaders;
};

export const getContent = (key: string, missingMessage?: string): string => {
  if (missingMessage == null) {
    missingMessage = `<i>missing ${key}</i>`;
  }
  let message = '';
  const generalContent = store.get(StoreKeys.generalContent);
  const generalContentKey = getKeyCaseInsensitive(generalContent, key);
  if (generalContentKey) {
    message = generalContent[generalContentKey];
  } else {
    console.info(missingMessage);
  }
  return message;
};

export const removeWhiteSpace = (s?: string): string => {
  if (s === null) {
    return null;
  }
  return s.replace(/\s/gm, '');
};

export const renderContentBelowCards = (products: ProductCatalogueProduct[]): JSX.Element => {
  const selectedPlanCategory = store.get(StoreKeys.selectedPlanCategory);
  const isCampaignPage = store.get(StoreKeys.isCampaignPage);
  if (selectedPlanCategory) {
    const key = isCampaignPage
      ? `btL_BelowCards_${removeWhiteSpace(selectedPlanCategory)}`
      : `Belowcards_${removeWhiteSpace(selectedPlanCategory)}`;
    let message = getContent(key);
    if (isCampaignPage) {
      message = getBelowCardsMessageforCampaign(message, products);
    }
    return <message-wrapper message={message} />;
  }
  return null;
};

export const getBelowCardsMessageforCampaign = (message: string, products: ProductCatalogueProduct[]): string => {
  const generalContent = store.get(StoreKeys.generalContent);
  const cancellationFeeBelowCards = Math.round(parseFloat(generalContent?.cancellationFeeBelowCards));

  const minCostPrices = products?.map((p) => ({
    name: p?.content?.productName,
    offerPrice: cancellationFeeBelowCards + Math.round(parseFloat(p?.content?.bundleOfferPrice)),
    withoutOfferPrice: cancellationFeeBelowCards + Math.round(parseFloat(p?.content?.withoutOfferPrice))
  }));

  const LowestPricesReplacements = minCostPrices?.map((p) => {
    if (p?.offerPrice === p?.withoutOfferPrice) {
      return `<b>${p?.name}</b>: $${p?.offerPrice}.`;
    }
    return `<b>${p?.name}</b>: $${p?.offerPrice}, or $${p?.withoutOfferPrice} if you don’t have AGL energy.`;
  });

  message = message.replace('${CancellationFee}', `$${cancellationFeeBelowCards}`);
  message = message.replace('${LowestPrices}', LowestPricesReplacements?.join(' '));

  return message;
};

export const getKeyCaseInsensitive = (object: unknown, key: string): string => {
  const asLowercase = key.toLowerCase();
  return Object.keys(object).find((k) => k.toLowerCase() === asLowercase);
};

export const replacePlaceholders = (message: string, replacements: { [key: string]: string }): string => {
  Object.entries(replacements).map(([k, v]) => (message = message.replace(`{${k}}`, v)));
  return message;
};

export const getWithoutOfferStrip = (product: ProductCatalogueProduct): boolean => {
  if (
    (product?.content?.withoutOfferPrice?.trim()?.length > 0 || product?.price?.trim()?.length > 0) &&
    product?.content?.withoutOfferText?.trim()?.length > 0
  ) {
    return product?.content?.withoutOfferPrice !== '0' || product?.price !== '0';
  }
  return false;
};

export const mergeProductDetailsWithNbnCommonLinks = (commonLink: CommonLink): GeneralContentCategoryItems[] => {
  const productCategory = store.get(StoreKeys.productCategory);
  if (commonLink && productCategory === 'nbn') {
    const techType = (store.get(StoreKeys.productDetails) as any)?.serviceQualification?.nbn?.serviceType;
    const selectedPlanCategory = store.get(StoreKeys.selectedPlanCategory);
    let category = 'nbn';
    if (techType === 'WIRELESS') {
      category = 'fixedWireless';
    } else if (selectedPlanCategory === 'Fibre upgrade') {
      category = 'fibreUpgrade';
    }
    const mappedCommonLinks = [];
    Object.keys(commonLink).forEach((key) => {
      const generalContent = commonLink[key]?.data?.find(
        (content: GeneralContent) => content?.content?.category?.toLowerCase() === category.toLowerCase()
      );
      /* istanbul ignore next */
      if (generalContent) {
        mappedCommonLinks.push({
          category,
          links: [
            {
              title: generalContent?.content.label,
              url: generalContent?.content.link
            }
          ]
        });
      } else if (commonLink[key].data[0]) {
        mappedCommonLinks.push({
          category,
          links: [
            {
              title: commonLink[key].data[0]?.content.label,
              url: commonLink[key].data[0]?.content.link
            }
          ]
        });
      }
    });
    return [...mappedCommonLinks];
  }
  return [];
};
